import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Activity, ArrowDownRight, ArrowUpRight, CalendarCheck2, CalendarRange,
    CheckCircle2, Clock3, Filter, IdCard, Percent, RefreshCw, Sparkles,
    Store, Users,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import BrandLoader from '../../components/BrandLoader';
import Navbar from '../../components/Navbar';
import DatePicker from '../../components/DatePicker';
import API from '../../utils/api';
import { useAdmin } from '../../context/AdminContext';
import toast from 'react-hot-toast';
import { CanvasDonutChart, CanvasLineChart } from '../Analytics/CanvasCharts';
import './Dashboard.css';

const REFRESH_SECONDS = 30;
const COLORS = ['#8b5cf6', '#10b981', '#f59e0b', '#ec4899', '#0ea5e9', '#f97316'];
const pad = value => String(value).padStart(2, '0');
const dateKey = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const daysAgo = days => {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() - (days - 1));
    return dateKey(date);
};
const TODAY = dateKey(new Date());
const number = value => Number(value || 0).toLocaleString('en-IN');
const pctChange = (current, previous) => previous ? Math.round(((current - previous) / previous) * 100) : current ? 100 : 0;

const Trend = ({ current, previous }) => {
    const value = pctChange(current, previous);
    const up = value >= 0;
    const Icon = up ? ArrowUpRight : ArrowDownRight;
    return <span className={`dashboard-trend ${up ? 'is-up' : 'is-down'}`}><Icon size={13} />{Math.abs(value)}%</span>;
};

const Dashboard = () => {
    const { admin } = useAdmin();
    const [stats, setStats] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [startDate, setStartDate] = useState(daysAgo(14));
    const [endDate, setEndDate] = useState(TODAY);
    const [draftStart, setDraftStart] = useState(daysAgo(14));
    const [draftEnd, setDraftEnd] = useState(TODAY);
    const [comparison, setComparison] = useState(true);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [nextRefresh, setNextRefresh] = useState(REFRESH_SECONDS);
    const mounted = useRef(true);
    const hasLoaded = useRef(false);

    const fetchDashboard = useCallback(async (silent = false) => {
        if (!silent) setRefreshing(true);
        try {
            const [statsResponse, analyticsResponse] = await Promise.all([
                API.get('/admin/stats'),
                API.get('/admin/analytics', { params: { startDate, endDate } }),
            ]);
            if (!mounted.current) return;
            setStats(statsResponse.data.stats || {});
            setAnalytics(analyticsResponse.data.analytics || {});
            setLastUpdated(new Date());
            setNextRefresh(REFRESH_SECONDS);
            hasLoaded.current = true;
        } catch (error) {
            if (!silent || !hasLoaded.current) toast.error('Failed to refresh dashboard');
        } finally {
            if (mounted.current) {
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, [startDate, endDate]);

    useEffect(() => {
        mounted.current = true;
        fetchDashboard(true);
        const refreshTimer = window.setInterval(() => {
            if (document.visibilityState === 'visible') fetchDashboard(true);
        }, REFRESH_SECONDS * 1000);
        const countdownTimer = window.setInterval(() => {
            if (document.visibilityState === 'visible') setNextRefresh(value => value > 1 ? value - 1 : REFRESH_SECONDS);
        }, 1000);
        const onVisibility = () => {
            if (document.visibilityState === 'visible') fetchDashboard(true);
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            mounted.current = false;
            window.clearInterval(refreshTimer);
            window.clearInterval(countdownTimer);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [fetchDashboard]);

    const rangeDays = Math.round((new Date(`${endDate}T12:00:00`) - new Date(`${startDate}T12:00:00`)) / 86400000) + 1;
    const period = analytics?.periodStats || {};
    const series = useMemo(() => (analytics?.dailySeries || []).map(item => ({
        date: item.date, label: item.label, members: item.users || 0, bookings: item.bookings || 0,
    })), [analytics]);
    const religionData = useMemo(() => (analytics?.religionStats || [])
        .filter(item => item._id)
        .sort((a, b) => b.count - a.count)
        .slice(0, 6)
        .map((item, index) => ({ name: item._id, value: item.count || 0, color: COLORS[index % COLORS.length] })), [analytics]);
    const genderData = useMemo(() => (analytics?.genderStats || []).map(item => ({
        name: item._id === 'Female' ? 'Brides' : item._id === 'Male' ? 'Grooms' : (item._id || 'Other'),
        value: item.count || 0,
        color: item._id === 'Female' ? '#ec4899' : item._id === 'Male' ? '#0ea5e9' : '#8b5cf6',
    })), [analytics]);

    const premiumRate = period.users ? Math.round(((period.premiumUsers || 0) / period.users) * 1000) / 10 : 0;
    const averageDaily = rangeDays ? Math.round((((period.users || 0) + (period.bookings || 0)) / rangeDays) * 10) / 10 : 0;
    const peakDay = series.reduce((best, item) => (item.members + item.bookings) > (best.members + best.bookings) ? item : best, series[0] || { label: '—', members: 0, bookings: 0 });
    const firstName = (admin?.name || 'Admin').split(' ')[0];
    const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    const kpis = [
        { label: 'Members joined', value: period.users, total: `${number(stats?.totalUsers)} total members`, current: period.users, previous: period.previousUsers, Icon: Users, tone: 'primary' },
        { label: 'Profiles created', value: period.profiles, total: `${number(stats?.totalProfiles)} total profiles`, current: period.profiles, previous: period.previousProfiles, Icon: IdCard, tone: 'violet' },
        { label: 'Bookings received', value: period.bookings, total: `${number(stats?.totalBookings)} all-time bookings`, current: period.bookings, previous: period.previousBookings, Icon: CalendarCheck2, tone: 'green' },
        { label: 'Services added', value: period.services, total: `${number(stats?.totalServices)} active listings`, current: period.services, previous: period.previousServices, Icon: Store, tone: 'amber' },
    ];

    const setQuickRange = days => {
        const nextStart = daysAgo(days);
        setDraftStart(nextStart);
        setDraftEnd(TODAY);
        setStartDate(nextStart);
        setEndDate(TODAY);
    };

    const applyRange = () => {
        if (!draftStart || !draftEnd) return toast.error('Choose both dates');
        if (draftStart > draftEnd) return toast.error('Start date must be before the end date');
        const days = Math.round((new Date(draftEnd) - new Date(draftStart)) / 86400000) + 1;
        if (days > 366) return toast.error('Choose a date range of one year or less');
        setStartDate(draftStart);
        setEndDate(draftEnd);
    };

    return (
        <div className="dashboard-layout">
            <Sidebar />
            <main className="admin-content dashboard-content">
                <Navbar />
                <div className="dashboard-page">
                    <header className="dashboard-heading">
                        <div>
                            <div className="dashboard-eyebrow"><Sparkles size={14} /> Command center</div>
                            <h1>Welcome back, {firstName}</h1>
                            <p>{today} · Here is what is happening across your platform.</p>
                        </div>
                        <div className="dashboard-live-actions">
                            <div className="dashboard-live"><span /><b>Live</b><small>{lastUpdated ? lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : 'Connecting'}</small></div>
                            <button className="dashboard-refresh" onClick={() => fetchDashboard(false)} disabled={refreshing}>
                                <RefreshCw size={16} className={refreshing ? 'is-spinning' : ''} /> Refresh
                            </button>
                        </div>
                    </header>

                    <section className="dashboard-filters" aria-label="Dashboard filters">
                        <div className="dashboard-filter-title"><CalendarRange size={17} /><span><b>Reporting period</b><small>{rangeDays} days selected</small></span></div>
                        <div className="dashboard-quick-ranges">
                            {[7, 14, 30, 90].map(days => <button key={days} className={rangeDays === days ? 'is-active' : ''} onClick={() => setQuickRange(days)}>{days}D</button>)}
                        </div>
                        <div className="dashboard-date-fields">
                            <label><span>From</span><DatePicker value={draftStart} onChange={setDraftStart} placeholder="Start date" /></label>
                            <label><span>To</span><DatePicker value={draftEnd} onChange={setDraftEnd} placeholder="End date" /></label>
                        </div>
                        <button className="dashboard-apply" onClick={applyRange} disabled={draftStart === startDate && draftEnd === endDate}><Filter size={15} /> Apply</button>
                        <label className="dashboard-comparison">
                            <span>Compare</span>
                            <button role="switch" aria-checked={comparison} className={comparison ? 'is-on' : ''} onClick={() => setComparison(value => !value)}><i /></button>
                        </label>
                    </section>

                    {loading ? (
                        <div className="dashboard-loading"><BrandLoader size={60} /><b>Loading live dashboard</b><span>Collecting the latest platform activity…</span></div>
                    ) : (
                        <>
                            <section className="dashboard-kpis">
                                {kpis.map(kpi => (
                                    <article className={`dashboard-kpi dashboard-kpi--${kpi.tone}`} key={kpi.label}>
                                        <div className="dashboard-kpi__head"><span><kpi.Icon size={18} /></span>{comparison && <Trend current={kpi.current} previous={kpi.previous} />}</div>
                                        <strong>{number(kpi.value)}</strong>
                                        <h2>{kpi.label}</h2>
                                        <p>{kpi.total}{comparison ? ' · vs previous period' : ''}</p>
                                    </article>
                                ))}
                            </section>

                            <section className="dashboard-chart-grid">
                                <article className="dashboard-card dashboard-card--growth">
                                    <div className="dashboard-card__header">
                                        <div><span><Activity size={17} /></span><div><h2>Growth activity</h2><p>Daily member registrations and bookings</p></div></div>
                                        <small>{startDate} → {endDate}</small>
                                    </div>
                                    <div className="dashboard-chart-metrics">
                                        <div><span>Members</span><b>{number(period.users)}</b>{comparison && <Trend current={period.users} previous={period.previousUsers} />}</div>
                                        <div><span>Bookings</span><b>{number(period.bookings)}</b>{comparison && <Trend current={period.bookings} previous={period.previousBookings} />}</div>
                                        <div className="dashboard-chart-key"><i />Members <i />Bookings</div>
                                    </div>
                                    <CanvasLineChart data={series} className="dashboard-line-canvas" />
                                </article>

                                <article className="dashboard-card dashboard-card--community">
                                    <div className="dashboard-card__header">
                                        <div><span><Users size={17} /></span><div><h2>Community mix</h2><p>Profiles created in this period</p></div></div>
                                    </div>
                                    {religionData.length ? (
                                        <>
                                            <CanvasDonutChart data={religionData} centerLabel="Profiles" className="dashboard-donut-canvas" />
                                            <div className="dashboard-legend">
                                                {religionData.slice(0, 5).map(item => <div key={item.name}><span><i style={{ background: item.color }} />{item.name}</span><b>{number(item.value)}</b></div>)}
                                            </div>
                                        </>
                                    ) : <div className="dashboard-empty">No profile data in this range</div>}
                                </article>
                            </section>

                            <section className="dashboard-bottom-grid">
                                <article className="dashboard-card dashboard-card--audience">
                                    <div className="dashboard-card__header"><div><span><IdCard size={17} /></span><div><h2>Audience split</h2><p>Member distribution by profile type</p></div></div></div>
                                    {genderData.some(item => item.value) ? (
                                        <div className="dashboard-audience-body">
                                            <CanvasDonutChart data={genderData} centerLabel="Members" className="dashboard-audience-canvas" />
                                            <div className="dashboard-audience-list">
                                                {genderData.map(item => <div key={item.name}><span><i style={{ background: item.color }} />{item.name}</span><b>{number(item.value)}</b></div>)}
                                            </div>
                                        </div>
                                    ) : <div className="dashboard-empty">No member data in this range</div>}
                                </article>

                                <article className="dashboard-card dashboard-card--pulse">
                                    <div className="dashboard-card__header"><div><span><Activity size={17} /></span><div><h2>Period pulse</h2><p>At-a-glance performance signals</p></div></div></div>
                                    <div className="dashboard-pulse-list">
                                        <div><span className="is-violet"><Percent size={17} /></span><p><b>{premiumRate}%</b><small>Premium conversion</small></p></div>
                                        <div><span className="is-green"><CheckCircle2 size={17} /></span><p><b>{averageDaily}</b><small>Average activities per day</small></p></div>
                                        <div><span className="is-amber"><Clock3 size={17} /></span><p><b>{peakDay.label}</b><small>Peak activity day · {peakDay.members + peakDay.bookings} events</small></p></div>
                                    </div>
                                </article>

                                <article className="dashboard-card dashboard-card--health">
                                    <div className="dashboard-health-icon"><Activity size={20} /></div>
                                    <div><span>System status</span><h2>All insights are live</h2><p>Automatic refresh in {nextRefresh}s. Your selected reporting period remains active.</p></div>
                                    <i className="dashboard-health-dot" />
                                </article>
                            </section>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Dashboard;
